/**
 * 인라인 HTML 살균기 (sd-select 등의 HTML 라벨용) — Stencil sanitize-inline-html 포팅.
 * 허용 인라인 태그(A/B/STRONG/I/EM/BR/SPAN) + class 속성만 남기고, 위험 태그/스킴을 제거한다.
 * DOM(template) 기반이라 브라우저에서만 살균; SSR(document 없음)에서는 전체 escape.
 */
const ALLOWED_INLINE_TAGS = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'BR', 'SPAN']);

const ALLOWED_ATTRS_BY_TAG: Record<string, Set<string>> = {
  A: new Set(['href', 'target', 'rel', 'class']),
};
const COMMON_ALLOWED_ATTRS = new Set(['class']);

// javascript:, data: 등 위험 프로토콜 차단 — 상대 URL과 안전한 스킴만 허용
const SAFE_HREF_RE = /^(https?:|mailto:|tel:|\/|#|\.)/i;

const DROP_CONTENT_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'META',
  'LINK',
  'BASE',
  'NOSCRIPT',
  // SVG/MATH 는 foreign-content 파싱 모드로 전환되어 재직렬화 시 다른 트리로
  // 재파싱될 수 있다(mXSS 벡터). 인라인 라벨에 필요 없으므로 통째로 제거한다.
  'SVG',
  'MATH',
  'TEMPLATE',
]);

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeNode = (node: ChildNode) => {
  if (node.nodeType === Node.COMMENT_NODE) {
    node.remove();
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as HTMLElement;
  const tagName = element.tagName;

  if (DROP_CONTENT_TAGS.has(tagName)) {
    element.remove();
    return;
  }

  Array.from(element.childNodes).forEach(child => sanitizeNode(child));

  if (ALLOWED_INLINE_TAGS.has(tagName)) {
    // React HTML 문자열 호환: className → class 정규화
    if (element.hasAttribute('className')) {
      const val = element.getAttribute('className')!;
      element.removeAttribute('className');
      if (!element.hasAttribute('class')) element.setAttribute('class', val);
    }
    const allowedAttrs = ALLOWED_ATTRS_BY_TAG[tagName] ?? COMMON_ALLOWED_ATTRS;
    Array.from(element.attributes).forEach(attr => {
      if (!allowedAttrs.has(attr.name)) element.removeAttribute(attr.name);
    });
    if (tagName === 'A') {
      const href = element.getAttribute('href');
      if (href !== null && href !== '' && !SAFE_HREF_RE.test(href)) element.removeAttribute('href');
      // target(특히 _blank) 이 있으면 reverse-tabnabbing 방지를 위해 rel 을 강제한다.
      if (element.hasAttribute('target')) {
        element.setAttribute('rel', 'noopener noreferrer');
      }
    }
    return;
  }

  // 허용 안 된 태그: 태그만 벗기고 자식은 보존 (unwrap)
  const parent = element.parentNode;
  if (parent == null) return;
  while (element.firstChild) parent.insertBefore(element.firstChild, element);
  parent.removeChild(element);
};

export const sanitizeInlineHtml = (value: string): string => {
  if (typeof document === 'undefined') return escapeHtml(value);
  const template = document.createElement('template');
  template.innerHTML = value;
  Array.from(template.content.childNodes).forEach(child => sanitizeNode(child));
  return template.innerHTML;
};
